import type { WidgetConfig } from '../ui/types.js';

/** Carries the HTTP status so callers can tell "session expired" from "offline". */
export class WidgetApiError extends Error {
  status: number;
  body: any;
  constructor(status: number, body: any) {
    super(`${status}`);
    this.name = 'WidgetApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * The page the visitor is looking at, read at the moment they send.
 *
 * Not read once at load: on a site that routes without reloading, someone can
 * wander from pricing to checkout mid-conversation, and the page worth showing
 * an agent is the one the question was asked from.
 */
function pageContext(): { page_url: string; page_title: string } {
  return { page_url: location.href, page_title: document.title };
}

/** What GET /api/widget/config answers with. */
export interface RemoteConfigResponse {
  allowed: boolean;
  host: string | null;
  /** `enabled` is a gate rather than a render option, so it is not on WidgetConfig. */
  config?: Partial<WidgetConfig> & { enabled?: boolean };
  error?: string;
  message?: string;
  adminUrl?: string;
}

/**
 * Ask the backend how this widget should look and whether this site may use it.
 *
 * Standalone rather than a method on ApiClient: it runs before there is a
 * config to construct one with, and needs no visitor token.
 */
export async function fetchRemoteConfig(apiUrl: string): Promise<RemoteConfigResponse | null> {
  try {
    const r = await fetch(`${apiUrl}/api/widget/config`, { credentials: 'omit' });
    // 403 carries the "not verified" explanation, so it is parsed like a
    // success rather than thrown away.
    if (!r.ok && r.status !== 403) return null;
    return (await r.json()) as RemoteConfigResponse;
  } catch {
    // Offline, blocked, or the API is down. The caller falls back to whatever
    // the page configured; failing to draw at all would be worse.
    return null;
  }
}

export class ApiClient {
  constructor(private config: WidgetConfig, private token?: string) {}

  setToken(t: string | undefined) {
    this.token = t;
  }

  private headers(json = true): Record<string, string> {
    const h: Record<string, string> = {};
    if (json) h['content-type'] = 'application/json';
    if (this.token) h['authorization'] = `Bearer ${this.token}`;
    return h;
  }

  async react(messageId: string, emoji: string): Promise<void> {
    await fetch(`${this.config.apiUrl}/api/messages/${messageId}/react`, {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify({ emoji }),
    }).catch(() => undefined);
  }

  async deleteMessage(messageId: string, scope: 'me' | 'everyone'): Promise<{ ok: boolean }> {
    const path = scope === 'me' ? 'delete-for-me' : 'delete-for-everyone';
    const r = await fetch(`${this.config.apiUrl}/api/messages/${messageId}/${path}`, {
      method: 'POST',
      headers: this.headers(),
    });
    return { ok: r.ok };
  }

  async markRead(conversationId: string): Promise<void> {
    await fetch(`${this.config.apiUrl}/api/messages/read/visitor`, {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify({ conversation_id: conversationId }),
      // Read receipts are best-effort — never let one break the chat.
    }).catch(() => undefined);
  }

  async requestOtp(email: string): Promise<{ ok: boolean; error?: string; retryAfterSeconds?: number }> {
    const r = await fetch(`${this.config.apiUrl}/api/visitor/auth/otp/request`, {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify({ email }),
    });
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      return { ok: false, error: body.error, retryAfterSeconds: body.retryAfterSeconds };
    }
    return { ok: true };
  }

  async verifyOtp(name: string, email: string, code: string): Promise<{ token: string; visitorId: string } | { error: string }> {
    const r = await fetch(`${this.config.apiUrl}/api/visitor/auth/otp/verify`, {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify({ name, email, code }),
    });
    const body = await r.json().catch(() => ({}));
    if (!r.ok) return { error: body.error ?? 'unknown' };
    return { token: body.token, visitorId: body.visitorId };
  }

  async openConversation(): Promise<{ id: string; resumed: boolean }> {
    const r = await fetch(`${this.config.apiUrl}/api/conversations`, { method: 'POST', headers: this.headers() });
    // Without this, an error response falls through to `body.conversation.id`
    // and throws a TypeError, leaving the panel half-rendered.
    if (!r.ok) throw new WidgetApiError(r.status, await r.json().catch(() => ({})));
    const body = await r.json();
    return { id: body.conversation.id, resumed: body.resumed };
  }

  /** Every conversation this visitor has, on any site. Empty on any failure. */
  async listMine(): Promise<
    Array<{
      id: string;
      domain?: string | null;
      last_message_at?: string | null;
      created_at?: string | null;
      /** Agent messages this visitor has not opened yet. */
      unread?: number;
    }>
  > {
    try {
      const r = await fetch(`${this.config.apiUrl}/api/conversations/mine`, { headers: this.headers(false) });
      if (!r.ok) return [];
      const body = await r.json();
      return Array.isArray(body.conversations) ? body.conversations : [];
    } catch {
      return [];
    }
  }

  async listMessages(conversationId: string): Promise<any[]> {
    const r = await fetch(`${this.config.apiUrl}/api/messages/${conversationId}`, { headers: this.headers(false) });
    // `body.messages ?? []` on a 401/404 silently rendered an *empty* thread,
    // which looked exactly like "my chat history disappeared".
    if (!r.ok) throw new WidgetApiError(r.status, await r.json().catch(() => ({})));
    const body = await r.json();
    return body.messages ?? [];
  }

  async sendText(conversationId: string, body: string, replyTo?: string): Promise<any> {
    const r = await fetch(`${this.config.apiUrl}/api/messages/visitor`, {
      method: 'POST',
      headers: this.headers(),
      body: JSON.stringify({
        conversation_id: conversationId,
        body,
        reply_to: replyTo,
        ...pageContext(),
      }),
    });
    // A refusal used to be read as a reply: the message vanished from the box
    // and nothing said it had not been sent.
    if (!r.ok) throw new WidgetApiError(r.status, await r.json().catch(() => ({})));
    return (await r.json()).message;
  }

  async uploadFile(conversationId: string, file: Blob, options: { filename: string; durationMs?: number }): Promise<any> {
    const fd = new FormData();
    fd.append('conversation_id', conversationId);
    if (options.durationMs != null) fd.append('duration_ms', String(options.durationMs));
    const page = pageContext();
    fd.append('page_url', page.page_url);
    fd.append('page_title', page.page_title);
    fd.append('file', file, options.filename);
    const r = await fetch(`${this.config.apiUrl}/api/uploads/visitor`, {
      method: 'POST',
      headers: this.headers(false),
      body: fd,
    });
    if (!r.ok) throw new WidgetApiError(r.status, await r.json().catch(() => ({})));
    return (await r.json()).message;
  }
}
